﻿import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { hydrateSnapshot } from '@/app/store/stateSnapshot/hydrateSnapshotAction';
import { logout } from '@/features/auth/authSlice';

import type { InventorySelectionStatus } from '@/features/inventory/types';
/* THIS STORE USED FOR MANAGING THE ACTIVE MENU AND RESOURCE ACTIONS */
export interface InventoryNavIntent {
    /** The tab to activate on the Shipment Inventory page. */
    tab: InventorySelectionStatus;
    /** ISO date-string range [from, to] to pre-set the date filter. */
    dateRange: [string, string];
}
interface ActiveMenuState {
    activeComponent: string;
    activeResourceActions?: string[];
    /** Changes on every menu selection, including selecting the current page again. */
    navigationRevision: number;
    /** True once the async AES-GCM snapshot has been dispatched at startup. */
    isSnapshotHydrated: boolean;
    /** Set by the dashboard before navigating to Shipment Inventory; consumed + cleared on arrival. */
    inventoryNavIntent: InventoryNavIntent | null;
}
const initialState: ActiveMenuState = {
    activeComponent: '',
    activeResourceActions: [],
    // Starts at zero and increments on menu selection; MainLayout combines it with the service-point key to remount page content.
    navigationRevision: 0,
    isSnapshotHydrated: false,
    inventoryNavIntent: null,
};
const activeMenuSlice = createSlice({
    name: 'activeMenu',
    initialState,
    reducers: {
        setActiveMenu(state, action: PayloadAction<string>) {
            state.activeComponent = action.payload;
            // Increment even when the current menu is selected again. MainLayout includes this
            // value in the React key, forcing page content to remount and reset local UI state.
            state.navigationRevision += 1;
        },
        setActiveResourceActions(state, action: PayloadAction<string[]>) {
            state.activeResourceActions = action.payload;
        },
        resetActiveMenuResources(state) {
            state.activeComponent = '';
            state.activeResourceActions = [];
        },
        setInventoryNavIntent(state, action: PayloadAction<InventoryNavIntent>) {
            state.inventoryNavIntent = action.payload;
        },
        clearInventoryNavIntent(state) {
            state.inventoryNavIntent = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout, (state) => {
            state.activeComponent = '';
            state.activeResourceActions = [];
            state.navigationRevision = 0;
            state.inventoryNavIntent = null;
        });
        builder.addCase(hydrateSnapshot, (state) => {
            // Mark hydration complete regardless of whether there was persisted data.
            state.isSnapshotHydrated = true;
            // Navigation belongs to this tab. Ignore activeMenuStore in legacy snapshots.
        });
    },
});
export const {
    setActiveMenu,
    setActiveResourceActions,
    resetActiveMenuResources,
    setInventoryNavIntent,
    clearInventoryNavIntent,
} = activeMenuSlice.actions;
export default activeMenuSlice.reducer;
